import React, { useEffect, useRef, useMemo } from 'react'
import { contactNumbers } from '@adc/constants'
import {
  Button,
  Icon,
  AiChatDrawer,
  useApplicationContext,
  constants,
  hooks,
  chatUtils,
  utils as coreUtils,
  PhoneVerification,
} from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import type { AiChatContext, AiChatMode, AiChatSubmitType } from '@adc/ui-adc'
import { Address } from 'common/components/address'
import { AiAssistantFlagData } from '../../types'
import type { CombinedListingType } from '../../context/listing'
import analytics from '../../services/analytics'

const EQUAL_WEB_BUTTON_ID = 'INDshadowRootWrap'

interface AiListingChatWrapperProps {
  listing: CombinedListingType
  setOpenChat: (open: boolean) => void
  openChat?: boolean
  setExternalPrompt: (prompt?: string) => void
  externalPrompt?: string
  externalMockResponse?: string
  mode: AiChatMode
}

/**
 * Wrapper around the AiChatDrawer to provide listing specific context to the AI chat.
 */

const AiListingChatWrapper = ({
  listing,
  setOpenChat,
  openChat,
  setExternalPrompt,
  externalPrompt,
  externalMockResponse,
  mode,
}: AiListingChatWrapperProps) => {
  const {
    graphWebsocket,
    user: untypedUser,
    featureFlags,
  } = useApplicationContext()
  const user = untypedUser
  const breakpoints = hooks.useBreakpoints()

  const isOnlineReo =
    listing.listing_configuration?.product_type ===
      graph.enums.ProductType.Day_1Reo ||
    listing.listing_configuration?.product_type === graph.enums.ProductType.Reo

  // Store the scroll position to restore it later
  const savedScrollPosition = useRef<number>(0)

  const onSend = (_message: string, submitType: AiChatSubmitType) => {
    analytics.aiAssistantChat.trackSubmitType(submitType)
  }

  const onConversationStart = (conversationUrn: string) => {
    analytics.aiAssistantChat.trackConversationStart(conversationUrn)
  }

  /**
   * In an ideal world, the PDP would not rely on the window (body element) to control scrolling, but
   * until we refactor the entire PDP to not need this, this will be manged here by using position fixed
   * on the body element when the chat is open in full screen mode to prevent scrolling.
   */
  useEffect(() => {
    const bodyElement = document?.body
    if (!bodyElement?.style) return

    const isFullScreen = breakpoints === 'mobile'

    if (isFullScreen && openChat) {
      // Save current scroll position and fix the body in place
      savedScrollPosition.current = window.scrollY
      bodyElement.style.position = 'fixed'
      bodyElement.style.top = `-${savedScrollPosition.current}px`
      bodyElement.style.width = '100%'
    } else {
      // Reset body styles and restore scroll position
      bodyElement.style.position = ''
      bodyElement.style.top = ''
      bodyElement.style.width = ''
      // Restore scroll position
      if (savedScrollPosition.current > 0) {
        window.scrollTo(0, savedScrollPosition.current)
        savedScrollPosition.current = 0
      }
    }
  }, [breakpoints, openChat])

  // Hide/show equal web button element when chat is opened
  useEffect(() => {
    const element = document.getElementById(EQUAL_WEB_BUTTON_ID)
    if (element) {
      element.style.display =
        openChat && breakpoints === 'mobile' ? 'none' : 'block'
    }
  }, [openChat, breakpoints])

  /**
   * Context isn't really used yet, but getting this passed in so we have it when we need it.
   */
  const context: AiChatContext = [
    {
      urn: listing.urn,
      url: listing.listing_page_path,
      linkText: Address.format(listing),
    },
  ]

  // @ts-ignore next
  const flagData = featureFlags['fe.resi.ai-assistant'] as
    | AiAssistantFlagData
    | undefined

  const liveAgentBannerFlag =
    !!featureFlags['fe.resi.enable-live-chat-banner-in-ai-chat']

  const enableLiveAgentBanner = useMemo(
    () => liveAgentBannerFlag && coreUtils.isDuringPacificBusinessHours(),
    [liveAgentBannerFlag]
  )

  const handleLiveAgentClick = () => {
    chatUtils.openChatPopup(user)
    analytics.aiAssistantChat.trackLiveAgentClick()
  }

  const handleLiveAgentBannerDisplayed = () => {
    analytics.aiAssistantChat.trackLiveAgentBannerDisplayed()
  }

  if (!flagData?.enabled) {
    return null
  }

  /**
   * At this point we are trusting that the mode has been set correctly based on the user's
   * feature flags and/or actions taken to open the chat.
   */
  return (
    <AiChatDrawer
      mode={mode === 'trace' ? 'chat' : mode}
      elmId='ai-chat'
      context={context}
      graphWebsocket={graphWebsocket}
      user={user}
      openChat={openChat || false}
      setOpenChat={setOpenChat}
      externalPrompt={externalPrompt}
      externalMockResponse={externalMockResponse}
      setExternalPrompt={setExternalPrompt}
      onExternalPromptProcessed={() => setExternalPrompt(undefined)}
      topMarginOnDesktop={92}
      hideChatFeedback={false}
      showProgress
      showPrivateMessages={false}
      introMessage={mode === 'mock' ? ' ' : undefined}
      onSend={onSend}
      onConversationStart={onConversationStart}
      enableLiveAgentBanner={enableLiveAgentBanner}
      onLiveAgentClick={handleLiveAgentClick}
      onLiveAgentBannerDisplayed={handleLiveAgentBannerDisplayed}
      feedbackPromptMoreHelpText={
        isOnlineReo ? (
          <a href={`tel:${contactNumbers.ONLINE_BUYER_EXPERIENCE}`}>
            {PhoneVerification.utils.formatPhoneNumber(
              String(contactNumbers.ONLINE_BUYER_EXPERIENCE)
            )}
          </a>
        ) : (
          <a href={`tel:${contactNumbers.FCL_ONLINE_CONTACT_NUMBER}`}>
            {PhoneVerification.utils.formatPhoneNumber(
              String(contactNumbers.FCL_ONLINE_CONTACT_NUMBER)
            )}
          </a>
        )
      }
    />
  )
}

// temporary button to open the chat
const { ColorNames } = constants

export const AiChatButton = ({
  setOpenChat,
  openChat,
}: {
  setOpenChat: AiListingChatWrapperProps['setOpenChat']
  openChat?: AiListingChatWrapperProps['openChat']
}) => {
  const { featureFlags } = useApplicationContext()

  // @ts-ignore next
  const flagData = featureFlags['fe.resi.ai-assistant'] as
    | AiAssistantFlagData
    | undefined

  return flagData?.enabled && flagData?.internalUser ? (
    <Button
      role='auxiliary'
      color={ColorNames.Info}
      onClick={() => {
        setOpenChat(!openChat)
      }}
      elmId='open-ai-chat'
      style={{
        zIndex: 30,
        padding: '5px 0 0 0',
        position: 'fixed',
        right: '-6px',
        minWidth: '37px',
        minHeight: '35px',
        top: 'calc(50% - 56px)',
      }}
      collapse
    >
      <span>
        <Icon name='sparkles' iconStyle='solid' color='initial' />
      </span>
    </Button>
  ) : null
}

export default AiListingChatWrapper
